import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@/contexts/PageContext';
import { usePartners, useFAQs, usePartnershipsAccess, canEditSection } from '@/hooks/usePartnerships';
import { FAQAccordion } from '@/components/partnerships/FAQAccordion';
import { FAQFormDialog } from '@/components/partnerships/FAQFormDialog';
import { PARTNER_CONFIG } from '@/components/partnerships/PartnerTheme';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { PartnerSlug, FAQ } from '../../../../shared/types/partnerships';

export function PartnerFAQPage() {
  const { t } = useTranslation();
  const { partner: slug } = useParams<{ partner: string }>();
  const navigate = useNavigate();
  const [editFAQ, setEditFAQ] = useState<FAQ | undefined>();
  const [createFAQ, setCreateFAQ] = useState(false);

  const { data: access } = usePartnershipsAccess();
  const { data: partners = [], isPending: partnersPending } = usePartners();
  const { setPartnerCtx } = usePageContext();

  const partnerSlug = slug as PartnerSlug;
  const config = PARTNER_CONFIG[partnerSlug];

  const partner = partners.find(p => p.slug === partnerSlug);
  const partnerId = partner?.partnerId || '';
  const canEdit = canEditSection(access?.editScopes, 'faq');

  useEffect(() => {
    if (!partnerId || !config) return;
    setPartnerCtx({ partnerId, partnerName: config.name, partnerSlug });
    return () => setPartnerCtx(null);
  }, [partnerId, partnerSlug]);

  const { data: faqs = [], isPending: listPending } = useFAQs(partnerId);
  // Mientras cargan los partners la query está deshabilitada y react-query v5 da isLoading=false:
  // sin esto se veía la lista vacía hasta recargar (feedback de Partnerships 07/10/2026).
  const isLoading = partnersPending || (!!partnerId && listPending);

  if (!config) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{t('partnerships.common.partner_not_found')}</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/partnerships')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.ui.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/partnerships/${partnerSlug}`} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: config.color }} />
          <h1 className="text-2xl font-bold tracking-tight">{config.name} — {t('partnerships.faq.title')}</h1>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setCreateFAQ(true)}>
            <Plus className="h-3.5 w-3.5" /> {t('partnerships.faq.new_button')}
          </Button>
        )}
      </div>

      {/* Accordion */}
      <div className="max-w-2xl">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><WinclapLoader /></div>
        ) : faqs.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">{t('partnerships.faq.empty')}</div>
        ) : (
          <FAQAccordion faqs={faqs} canEdit={canEdit} onEdit={setEditFAQ} />
        )}
      </div>

      {partnerId && (
        <FAQFormDialog
          open={createFAQ || !!editFAQ}
          onOpenChange={o => { if (!o) { setCreateFAQ(false); setEditFAQ(undefined); } }}
          partnerId={partnerId}
          faq={editFAQ}
        />
      )}
    </div>
  );
}
